import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { DataTable } from "../components/DataTable";
import { ReportBugDialog } from "../components/ReportBugDialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtDate } from "../lib/api";
import { trackRecent } from "../components/Layout";
import {
  IB_BUG_STATUSES, IB_BUG_TERMINAL, IB_IDEA_STATUSES, IB_IDEA_TERMINAL,
  IB_SEVERITIES, IB_PRIORITIES, IB_MODULE_AREAS, IB_STATUS_COLORS, IB_SEVERITY_COLORS,
} from "../lib/fields";
import { Bug, Lightbulb, ChevronUp, Plus } from "lucide-react";

const SEV_ORDER = { Critical: 0, Major: 1, Minor: 2, Cosmetic: 3 };

function StatusPill({ status }) {
  return <Badge className={`${IB_STATUS_COLORS[status] || "bg-slate-100 text-slate-600"} hover:bg-inherit`}>{status}</Badge>;
}

export default function IdeasBugs() {
  const { t, canWrite } = useApp();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/ideas").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [load]);

  const go = (r) => { trackRecent(r.ib_number, `/ideas/${r.id}`); navigate(`/ideas/${r.id}`); };

  const bugs = useMemo(() => rows.filter((r) => r.type === "Bug")
    .sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9) || (b.updated_at || "").localeCompare(a.updated_at || "")), [rows]);
  const ideas = useMemo(() => rows.filter((r) => r.type !== "Bug")
    .sort((a, b) => (b.votes || 0) - (a.votes || 0)), [rows]);

  const assignees = users.filter((u) => ["admin", "sales_manager"].includes(u.role));
  const commonAssignFilter = { key: "assigned_to", label: t("ib_assigned_to"), options: assignees.map((u) => u.name) };

  const bugColumns = [
    { key: "ib_number", label: "#", render: (r) => <span className="font-mono font-medium text-[#1B2A4A]">{r.ib_number}</span> },
    { key: "title", label: t("ib_title_field"), render: (r) => <span className="font-medium">{r.title}</span> },
    { key: "module_area", label: t("ib_module_area") },
    { key: "status", label: t("status"), render: (r) => <StatusPill status={r.status} /> },
    { key: "severity", label: t("severity"), render: (r) => <Badge className={`${IB_SEVERITY_COLORS[r.severity] || ""} hover:bg-inherit`}>{r.severity || "–"}</Badge> },
    { key: "assigned_to", label: t("ib_assigned_to"), render: (r) => r.assigned_to || "–" },
    { key: "updated_at", label: t("updated"), render: (r) => fmtDate(r.updated_at) },
  ];

  const ideaColumns = [
    { key: "ib_number", label: "#", render: (r) => <span className="font-mono font-medium text-[#1B2A4A]">{r.ib_number}</span> },
    { key: "title", label: t("ib_title_field"), render: (r) => (
      <span className="font-medium flex items-center gap-2">
        {r.type === "Feature Request" ? <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 text-[10px]">FR</Badge> : null}
        {r.title}
      </span>) },
    { key: "module_area", label: t("ib_module_area") },
    { key: "status", label: t("status"), render: (r) => <StatusPill status={r.status} /> },
    { key: "votes", label: t("ib_votes"), align: "right", render: (r) => (
      <span className="inline-flex items-center gap-1 font-mono font-semibold text-[#1B2A4A]"><ChevronUp className="h-3.5 w-3.5 text-[#F5A623]" />{r.votes || 0}</span>) },
    { key: "assigned_to", label: t("ib_assigned_to"), render: (r) => r.assigned_to || "–" },
    { key: "updated_at", label: t("updated"), render: (r) => fmtDate(r.updated_at) },
  ];

  return (
    <div className="space-y-4" data-testid="ideas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("ideas_bugs")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("ib_page_desc")}</p>
        </div>
        {canWrite && (
          <Button data-testid="ideas-new-btn" onClick={() => setDialogOpen(true)}
            className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
            <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("ib_report")}
          </Button>
        )}
      </div>

      <Tabs defaultValue="bugs">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="bugs" data-testid="ib-tab-bugs"><Bug className="h-3.5 w-3.5 mr-1.5" />{t("ib_bugs")} ({bugs.length})</TabsTrigger>
          <TabsTrigger value="ideas" data-testid="ib-tab-ideas"><Lightbulb className="h-3.5 w-3.5 mr-1.5" />{t("ib_ideas_features")} ({ideas.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="bugs" className="mt-4">
          <DataTable module="ideas-bugs" rows={bugs} columns={bugColumns} onRowClick={go}
            filters={[
              { key: "status", label: t("status"), options: [...IB_BUG_STATUSES, ...IB_BUG_TERMINAL] },
              { key: "severity", label: t("severity"), options: IB_SEVERITIES },
              { key: "priority", label: t("ib_priority"), options: IB_PRIORITIES },
              { key: "module_area", label: t("ib_module_area"), options: IB_MODULE_AREAS },
              commonAssignFilter,
            ]} />
        </TabsContent>

        <TabsContent value="ideas" className="mt-4">
          <DataTable module="ideas-features" rows={ideas} columns={ideaColumns} onRowClick={go}
            filters={[
              { key: "type", label: t("type"), options: ["Idea", "Feature Request"] },
              { key: "status", label: t("status"), options: [...IB_IDEA_STATUSES, ...IB_IDEA_TERMINAL] },
              { key: "priority", label: t("ib_priority"), options: IB_PRIORITIES },
              { key: "module_area", label: t("ib_module_area"), options: IB_MODULE_AREAS },
              commonAssignFilter,
            ]} />
        </TabsContent>
      </Tabs>

      <ReportBugDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={(item) => navigate(`/ideas/${item.id}`)} />
    </div>
  );
}
